import React, { Fragment, Suspense, useState } from "react";
import * as S from "./OverviewBrandPrinciples.styles";
import {
  IOverviewBrandPrinciplesProps,
  IPrincipleSection,
  SlideDirection,
} from "./OverviewBrandPrinciples.types";
import { useTabAutoplay } from "hooks/useTabAutoplay";
import { CarouselContent } from "./CarouselContent";
import { MicrositeHeading } from "pages/LayoutComponents/MicrositeHeading";
import { ProgressAnimationButton } from "components/ProgressAnimationButton";
import { useTranslation } from "hooks/useTranslation";
import { useMedia } from "react-use";
import { BREAKPOINTS } from "@ubnt/store.core/lib/constants/layout";
import { ArrowLeftPrimaryIcon, ArrowRightPrimaryIcon } from "@ubnt/icons";

const LottieAnimation = React.lazy(() =>
  import("./LottieAnimation").then(({ LottieAnimation }) => ({
    default: LottieAnimation,
  })),
);

export const OverviewBrandPrinciples = ({
  items,
  title,
  isInView,
}: Readonly<IOverviewBrandPrinciplesProps>) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState(items[0]);
  const [isAnimatedOnce, setIsAnimatedOnce] = useState(false);
  const [hadUserInteraction, setHadUserInteraction] = useState(false);
  const [slideDirection, setSlideDirection] = useState<SlideDirection>(
    SlideDirection.Right,
  );

  const isDesktopDown = useMedia(BREAKPOINTS.DESKTOP_AND_DOWN);

  const onUpdateTab = (value: IPrincipleSection) => {
    if (!hadUserInteraction) {
      setActiveTab(value);
    }

    if (!isAnimatedOnce) {
      setIsAnimatedOnce(true);
    }
  };

  useTabAutoplay({
    activeTab,
    setActiveTab: onUpdateTab,
    shouldCancelUpdate: !isInView || hadUserInteraction,
    sections: items,
  });

  const onTabSelect = (item: IPrincipleSection) => {
    setHadUserInteraction(true);

    if (item.id !== activeTab.id) {
      setActiveTab(item);
    }

    if (!isAnimatedOnce && item.id !== activeTab.id) {
      setIsAnimatedOnce(true);
    }
  };

  const switchNextTab = () => {
    setSlideDirection(SlideDirection.Right);
    const isLastIndex = activeTab?.index + 1 >= items.length;
    const correctedIndex = isLastIndex ? 0 : activeTab?.index + 1;
    onTabSelect(items[correctedIndex]);
  };

  const switchPreviousTab = () => {
    setSlideDirection(SlideDirection.Left);
    const isFirstIndex = activeTab?.index === 0;
    const correctedIndex = isFirstIndex
      ? items.length - 1
      : activeTab?.index - 1;
    onTabSelect(items[correctedIndex]);
  };

  return (
    <S.BrandPrincipleWrap>
      <MicrositeHeading title={title} isCentered isMainTitle />

      <S.DesktopOnly>
        <Tabs
          items={items}
          activeTab={activeTab}
          onTabSelect={onTabSelect}
          isInView={isInView}
        />
      </S.DesktopOnly>

      <S.SlideContentContainer>
        <S.SlideContentInsideWrap>
          <S.InvisibleButton
            aria-label="Switch to previous Brand Principle"
            onClick={switchPreviousTab}
          />

          <S.RelativeContainer>
            {items.map((item, index) => (
              <S.AnimationContainer
                key={`container-${item.id}-${index}`}
                $isActive={item.id === activeTab.id}
                $canAnimate={isAnimatedOnce}
                $slideDirection={slideDirection}
              >
                <CarouselContent item={item}>
                  <S.MobileImageWrapper>
                    <S.MobileImage
                      src={item.mobileBackground}
                      alt={t(item.title)}
                    />

                    <S.ArrowButtonLeft onClick={switchPreviousTab}>
                      <S.IconWrapper>
                        <ArrowLeftPrimaryIcon
                          size={24}
                          color="#808893"
                          style={{ marginRight: "2px" }}
                        />
                      </S.IconWrapper>
                    </S.ArrowButtonLeft>

                    <S.ArrowButtonRight>
                      <S.IconWrapper>
                        <ArrowRightPrimaryIcon
                          size={24}
                          color="#808893"
                          style={{ marginLeft: "2px" }}
                        />
                      </S.IconWrapper>
                    </S.ArrowButtonRight>
                  </S.MobileImageWrapper>
                </CarouselContent>
              </S.AnimationContainer>
            ))}
          </S.RelativeContainer>

          {!isDesktopDown && (
            <Suspense fallback={null}>
              <LottieAnimation
                animationData={activeTab.lottie.file}
                isInView={isInView}
              />
            </Suspense>
          )}

          <S.InvisibleButton
            aria-label="Switch to next Brand Principle"
            $isRight
            onClick={switchNextTab}
          />
        </S.SlideContentInsideWrap>
      </S.SlideContentContainer>

      <S.MobileOnly>
        <Tabs
          items={items}
          activeTab={activeTab}
          isInView
          onTabSelect={onTabSelect}
        />
      </S.MobileOnly>
    </S.BrandPrincipleWrap>
  );
};

const Tabs = ({
  items,
  activeTab,
  onTabSelect,
  isInView,
  spacing = { top: 24, bottom: 48 },
}: {
  items: IPrincipleSection[];
  activeTab: IPrincipleSection | null;
  isInView?: boolean;
  onTabSelect: (item: IPrincipleSection) => void;
  spacing?: {
    top: number;
    bottom: number;
  };
}) => {
  const { t } = useTranslation();

  return (
    <S.Container size="desktop" noVerticalMargin $spacing={spacing}>
      <S.TabContainer>
        {items.map((item, index) => (
          <Fragment key={`tab-fragment-${item.id}-${index}`}>
            <S.StatusBarContainer onClick={() => onTabSelect(item)}>
              <S.StyledStatusBar
                $isActive={isInView && activeTab?.id === item.id}
                $playbackLimit={activeTab?.playbackTimeInSeconds ?? 0}
              />
            </S.StatusBarContainer>

            <S.ButtonWrapper>
              <ProgressAnimationButton
                key={`tab-${item.id}-${index}`}
                isActive={activeTab?.id === item.id}
                canPlayAnimation={isInView && activeTab?.id === item.id}
                playbackTimeInSeconds={activeTab?.playbackTimeInSeconds ?? 0}
                text={t(item.title)}
                variant="secondary"
                handleClick={() => onTabSelect(item)}
              />
            </S.ButtonWrapper>
          </Fragment>
        ))}
      </S.TabContainer>
    </S.Container>
  );
};
